import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { FileText, ImageOff, Loader2, ExternalLink, Paperclip, Trash2, Upload, Mic } from "lucide-react";

const ACCEPT = "image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp3,.wav,.m4a,.aac,.ogg,.flac";

function EviThumb({ path, contentType, filename, onDelete }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const isImage = (contentType || "").startsWith("image/");
  const isAudio = (contentType || "").startsWith("audio/");
  useEffect(() => {
    if (!isImage && !isAudio) return;
    let url;
    client.get(`/incidents/attachment/${path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path, isImage, isAudio]);
  const open = async () => {
    try {
      const r = await client.get(`/incidents/attachment/${path}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Could not open file"); }
  };
  return (
    <div className="relative group">
      {isAudio ? (
        <div data-testid="cfs-evidence-audio"
          className="flex flex-col justify-center gap-1 px-2 py-1.5 h-20 border border-[#22D3EE]/50 bg-[#0d0e11] rounded-sm">
          <div className="flex items-center gap-1.5 text-[#22D3EE] text-[10px] font-mono">
            <Mic size={12} /> <span className="max-w-[190px] truncate" title={filename}>{filename || "Audio"}</span>
          </div>
          {failed ? <span className="text-[9px] font-mono text-[#EF4444]">Could not load audio</span>
            : src ? <audio controls src={src} preload="metadata" className="h-8 w-[230px]" data-testid="cfs-evidence-audio-player" />
            : <Loader2 size={13} className="animate-spin text-[#9CA3AF]" />}
        </div>
      ) : isImage ? (
        <button data-testid="cfs-evidence-image" onClick={open} title={filename}
          className="w-20 h-20 border border-[#2C2D35] bg-[#0d0e11] overflow-hidden flex items-center justify-center hover:border-[#3B82F6] transition-colors">
          {failed ? <ImageOff size={16} className="text-[#4B5563]" />
            : src ? <img src={src} alt={filename} className="w-full h-full object-cover" />
            : <Loader2 size={14} className="animate-spin text-[#9CA3AF]" />}
        </button>
      ) : (
        <button data-testid="cfs-evidence-file" onClick={open}
          className="flex items-center gap-1.5 px-2 py-2 h-20 border border-[#D9A441]/50 bg-[#0d0e11] text-[#D9A441] text-[10px] font-mono hover:bg-[#D9A441]/10">
          <FileText size={14} /> <span className="max-w-[100px] truncate">{filename || "File"}</span> <ExternalLink size={10} />
        </button>
      )}
      {onDelete && (
        <button data-testid="cfs-evidence-delete" title="Remove" onClick={() => onDelete(path, filename)}
          className="absolute -top-1.5 -right-1.5 bg-[#DC2626] text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

export default function IncidentEvidence({ incidentId, attachments = [], canManage = true, onChanged, compact }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await client.post(`/incidents/${incidentId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} file(s) attached to call`);
      onChanged?.();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async (path, filename) => {
    if (busy || !window.confirm(`Remove "${filename}" from this call?`)) return;
    setBusy(true);
    try { await client.delete(`/incidents/${incidentId}/attachments`, { params: { path } }); toast.success("Removed"); onChanged?.(); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Could not remove"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="cfs-evidence">
      <div className={`font-mono ${compact ? "text-[9px]" : "text-[10px]"} text-[#9CA3AF] uppercase mb-1.5 flex items-center gap-1`}>
        <Paperclip size={11} /> Evidence — Photos, Audio &amp; Documents ({attachments.length})
        {canManage && (
          <label data-testid="cfs-evidence-upload"
            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#3B82F6]/50 text-[#3B82F6] cursor-pointer hover:bg-[#3B82F6]/10">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Add Files
            <input type="file" multiple accept={ACCEPT} className="hidden" onChange={upload} disabled={busy} />
          </label>
        )}
      </div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((a, i) => (
            <EviThumb key={i} path={a.path} contentType={a.content_type} filename={a.filename}
              onDelete={canManage ? remove : undefined} />
          ))}
        </div>
      )}
    </div>
  );
}
